import { useQuery } from '@tanstack/react-query';
import { useEffect, type ReactElement } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { apiFetch } from '../../lib/api';
import { formatInrMinor } from '../../lib/constants';
import { useAuth } from '../../lib/auth';
import { ParentShell } from '../components/ParentShell';
import { learnerNavFromSummary } from '../lib/parent-portal-api';
import { fetchMyLearners } from '../lib/registration-api';
import { getLastLearnerId, setLastLearnerId } from '../lib/selected-learner';

/**
 * The parent home.
 *
 * Almost every parent has one child, so this is not a dashboard listing
 * children — it is one child's page, which changes character as the admission
 * progresses. The switcher only appears when there is more than one.
 *
 * The page opens with the answer to the only question a parent has: does
 * anything need me?
 */

type JourneyState = 'done' | 'now' | 'next' | 'later';

interface JourneyStep {
  key: string;
  title: string;
  state: JourneyState;
  detail: string | null;
}

interface ParentHome {
  children: Array<{
    learnerId: string;
    displayName: string;
    initials: string;
    applicationStatus: string;
    needsAction: boolean;
  }>;
  child: {
    learnerId: string;
    applicationId: string;
    displayName: string;
    enrolmentNumber: string | null;
    applicationStatus: string;
    documentsPolicy?: 'MANDATORY' | 'OPTIONAL';
    academicLevelName: string | null;
    schoolName: string | null;
    academicYearCode: string | null;
    classesStartOn: string | null;
    journey: JourneyStep[];
    counsellor: { name: string; role: string } | null;
    subjects: Array<{ subjectName: string; syllabusCode: string; groupName: string | null }>;
    subjectGroups: Array<{ name: string; covered: boolean }>;
    amountDue: {
      totalMinor: string;
      currency: string;
      lines: Array<{ description: string; amountMinor: string }>;
    } | null;
    payments: Array<{
      transactionId: string;
      paidAt: string;
      purpose: string;
      amountMinor: string;
      receiptNumber: string | null;
      receiptId: string | null;
    }>;
  } | null;
}

function fetchHome(learnerId?: string): Promise<ParentHome> {
  const qs = learnerId ? `?learnerId=${encodeURIComponent(learnerId)}` : '';
  return apiFetch<ParentHome>(`/me/home${qs}`);
}

function formatDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Whole days from today to the given date. Negative once it has passed. */
function daysUntil(value: string | null): number | null {
  if (!value) return null;
  const target = new Date(value);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  const startOfDay = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((startOfDay(target) - startOfDay(today)) / 86_400_000);
}

function paymentCta(
  child: NonNullable<ParentHome['child']>,
): { label: string; to: string } {
  const to = `/applications/${child.applicationId}/payment`;
  if (child.amountDue) {
    return { label: `Pay ${formatInrMinor(child.amountDue.totalMinor)}`, to };
  }
  return { label: 'Pay subject fees', to };
}

const PAYMENT_READY_STATUSES = new Set([
  'APPROVAL_COMPLETE',
  'QUOTATION_ISSUED',
  'PAYMENT_PENDING',
]);

/**
 * The headline — the whole point of the page.
 *
 * Written as sentences about the child, not status labels. "Her place is held
 * until 12 September" tells a parent what is happening and what is at stake;
 * "PAYMENT_PENDING" does not.
 */
function headlineFor(child: NonNullable<ParentHome['child']>): {
  tone: 'warn' | 'wait' | 'ok';
  title: string;
  body: string;
  cta: { label: string; to: string } | null;
} {
  const first = child.displayName.split(' ')[0] ?? 'Your child';
  const status = child.applicationStatus;

  if (PAYMENT_READY_STATUSES.has(status)) {
    const cta = paymentCta(child);
    return {
      tone: 'warn',
      title: `${first}'s place is held pending payment`,
      body: child.amountDue
        ? `${formatInrMinor(child.amountDue.totalMinor)} is due to confirm the admission. The subjects are approved and the seat is reserved.`
        : 'The subjects are approved and subject fees are ready to pay. Confirm the admission to secure the place.',
      cta,
    };
  }

  switch (status) {
    case 'DRAFT':
      return {
        tone: 'warn',
        title: `Finish ${first}'s registration`,
        body: 'The registration fee has not been paid yet, so the application has not reached us.',
        cta: { label: 'Continue', to: '/register' },
      };
    case 'REGISTRATION_PENDING_PAYMENT':
      return {
        tone: 'warn',
        title: `Finish ${first}'s registration`,
        body: 'The registration fee has not been paid yet, so the application has not reached us.',
        cta: {
          label: 'Pay registration fee',
          to: `/register/payment?applicationId=${child.applicationId}`,
        },
      };
    case 'REGISTERED':
      return {
        tone: 'warn',
        title: `Choose ${first}'s subjects`,
        body: 'Registration is complete. Once the subjects are chosen we can review them and confirm the fees.',
        cta: { label: 'Choose subjects', to: `/applications/${child.applicationId}/subjects` },
      };
    case 'CHANGES_REQUESTED':
      return {
        tone: 'warn',
        title: `We need a change to ${first}'s application`,
        body: 'Open the application to see what we have asked for.',
        cta: { label: 'See what is needed', to: `/children/${child.learnerId}` },
      };
    case 'AWAITING_PARENT_DECISION':
      return {
        tone: 'warn',
        title: `${first}'s subjects need your decision`,
        body: 'Some subjects were not approved. You can accept the approved ones or change the selection.',
        cta: { label: 'Review the decision', to: `/children/${child.learnerId}` },
      };
    case 'UNDER_REVIEW':
      return {
        tone: 'wait',
        title: `We're reviewing ${first}'s subject choices`,
        body: "We'll email you the moment there's a decision. Nothing is needed from you.",
        cta: null,
      };
    case 'ADMISSION_PROVISIONAL':
    case 'PAID':
      return {
        tone: 'ok',
        title: `${first}'s admission fee is paid`,
        body: child.documentsPolicy === 'OPTIONAL'
          ? 'Your place is reserved. Admission will complete without a document upload.'
          : 'Your place is confirmed.',
        // Document-upload module parked.
        // cta: child.documentsPolicy === 'OPTIONAL'
        //   ? null
        //   : { label: 'Upload documents', to: `/applications/${child.applicationId}/documents` },
        cta: null,
      };
    case 'ADMISSION_CONFIRMED':
      return {
        tone: 'ok',
        title: `${first}'s admission is confirmed`,
        body: child.enrolmentNumber
          ? `The enrolment number is ${child.enrolmentNumber} — you'll need it when contacting the school.`
          : 'Everything is settled.',
        cta: null,
      };
    case 'REJECTED':
      return {
        tone: 'wait',
        title: `${first}'s application was not successful`,
        body: 'Your admission counsellor can explain the decision and what options remain.',
        cta: null,
      };
    case 'WITHDRAWN':
    case 'EXPIRED':
    case 'CLOSED_ADMINISTRATIVELY':
    case 'ADMISSION_REVOKED':
      return {
        tone: 'wait',
        title: `${first}'s application is closed`,
        body: 'Speak to your admission counsellor if you think this is wrong.',
        cta: null,
      };
    default:
      return {
        tone: 'wait',
        title: `${first}'s application is in progress`,
        body: "Nothing is needed from you right now. We'll be in touch.",
        cta: null,
      };
  }
}

function navFromHomeChildren(
  children: ParentHome['children'],
): Array<{ learnerId: string; firstName: string; lastName: string; applicationStatus: string }> {
  return children.map((child) => {
    const parts = child.displayName.trim().split(/\s+/);
    return {
      learnerId: child.learnerId,
      firstName: parts[0] ?? '',
      lastName: parts.slice(1).join(' '),
      applicationStatus: child.applicationStatus,
    };
  });
}

export function ParentHomePage(): ReactElement {
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  const requested = params.get('child') ?? undefined;

  useEffect(() => {
    if (requested) return;
    const last = getLastLearnerId();
    if (!last) return;
    setParams((current) => {
      if (current.get('child') === last) return current;
      const next = new URLSearchParams(current);
      next.set('child', last);
      return next;
    }, { replace: true });
  }, [requested, setParams]);

  const learnersQuery = useQuery({
    queryKey: ['my-learners'],
    queryFn: fetchMyLearners,
    enabled: Boolean(token),
  });

  const homeQuery = useQuery({
    queryKey: ['parent-home', requested],
    queryFn: () => fetchHome(requested),
    enabled: Boolean(token),
  });

  const child = homeQuery.data?.child ?? null;

  useEffect(() => {
    if (child?.learnerId) setLastLearnerId(child.learnerId);
  }, [child?.learnerId]);

  if (!token) return <Navigate to="/login" replace />;

  const data = homeQuery.data;
  const siblings = data?.children ?? [];

  const sidebarLearners = (() => {
    const fromLearnersQuery = learnerNavFromSummary(learnersQuery.data ?? []);
    if (fromLearnersQuery.length > 0) return fromLearnersQuery;
    return navFromHomeChildren(siblings);
  })();

  if (homeQuery.isLoading) {
    return (
      <ParentShell learners={sidebarLearners}>
        <p className="text-muted">Loading…</p>
      </ParentShell>
    );
  }

  // A parent who has not registered anyone yet gets a door, not an empty shell.
  if (!child) {
    return (
      <ParentShell learners={sidebarLearners}>
        <section className="ph-empty">
          <h1>Welcome to Learn Plus</h1>
          <p>You haven't registered a child yet. It takes about ten minutes.</p>
          <Link className="btn btn-primary" to="/register">Register a child</Link>
        </section>
      </ParentShell>
    );
  }

  const headline = headlineFor(child);
  const days = daysUntil(child.classesStartOn);
  const admitted = child.applicationStatus === 'ADMISSION_CONFIRMED'
    || child.applicationStatus === 'ADMISSION_PROVISIONAL';
  const coveredGroups = child.subjectGroups.filter((g) => g.covered).length;

  const meta = [child.academicLevelName, child.schoolName, child.academicYearCode]
    .filter(Boolean)
    .join(' · ');

  return (
    <ParentShell learners={sidebarLearners}>
      {siblings.length > 1 ? (
        <nav className="ph-switcher" aria-label="Your children">
          {siblings.map((sibling) => (
            <button
              key={sibling.learnerId}
              type="button"
              className={`ph-sw${sibling.learnerId === child.learnerId ? ' is-on' : ''}`}
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set('child', sibling.learnerId);
                setLastLearnerId(sibling.learnerId);
                setParams(next, { replace: true });
              }}
            >
              <span className="ph-sw-av" aria-hidden="true">{sibling.initials}</span>
              {sibling.displayName.split(' ')[0]}
              {sibling.needsAction ? (
                <>
                  <span className="ph-sw-flag" aria-hidden="true" />
                  <span className="visually-hidden">needs your attention</span>
                </>
              ) : null}
            </button>
          ))}
          <Link className="ph-sw ph-sw-add" to="/register">+ Add a child</Link>
        </nav>
      ) : null}

      <header className="ph-hero">
        <p className="ph-eyebrow">
          {admitted
            ? (child.applicationStatus === 'ADMISSION_CONFIRMED' ? 'Admission confirmed' : 'Admission fee paid')
            : 'Admission'}
          {child.academicYearCode ? ` · ${child.academicYearCode}` : ''}
        </p>
        <h1 className="ph-name">{child.displayName}</h1>
        {meta ? (
          <p className="ph-meta">
            {meta}
            {child.enrolmentNumber ? (
              <> · <span className="ph-enrol">{child.enrolmentNumber}</span></>
            ) : null}
          </p>
        ) : null}
      </header>

      <section className={`ph-headline ph-${headline.tone}`}>
        <div className="ph-headline-text">
          <h2>{headline.title}</h2>
          <p>{headline.body}</p>
        </div>
        {headline.cta ? (
          <Link className="btn btn-primary" to={headline.cta.to}>{headline.cta.label}</Link>
        ) : null}
      </section>

      {admitted && days !== null && days > 0 ? (
        <section className="ph-section ph-countdown">
          <span className="ph-count-big">{days}</span>
          <span className="ph-count-unit">
            {days === 1 ? 'day' : 'days'} until the first class, on {formatDate(child.classesStartOn)}
          </span>
        </section>
      ) : null}

      <section className="ph-section">
        <h3>What happens next</h3>
        <p className="ph-hint">The admission, start to finish.</p>
        <ol className="ph-journey">
          {child.journey.map((step) => (
            <li key={step.key} className={`ph-step is-${step.state}`}>
              <span className="ph-dot" aria-hidden="true" />
              <p className="ph-step-title">{step.title}</p>
              {step.detail ? <p className="ph-step-meta">{formatDate(step.detail)}</p> : null}
              {/* Colour is never the only signal — every step states where it is. */}
              <span className="visually-hidden">
                {step.state === 'done' ? 'completed' : step.state === 'now' ? 'in progress' : 'not started'}
              </span>
              {step.state === 'now' && child.counsellor && step.key === 'review' ? (
                <span className="ph-who">
                  <span className="ph-who-av" aria-hidden="true">
                    {child.counsellor.name.split(' ').map((p) => p.charAt(0)).slice(0, 2).join('')}
                  </span>
                  {child.counsellor.name} is handling this
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      </section>

      {child.amountDue ? (
        <section className="ph-section">
          <h3>What you're paying for</h3>
          <p className="ph-hint">
            For the full year{child.academicYearCode ? `, ${child.academicYearCode}` : ''}.
          </p>
          <div className="ph-money">
            {child.amountDue.lines.map((line, index) => (
              <div className="ph-mrow" key={`${line.description}-${index}`}>
                <span>{line.description}</span>
                <span className="ph-num">{formatInrMinor(line.amountMinor)}</span>
              </div>
            ))}
            <div className="ph-mrow is-total">
              <span>Total due</span>
              <span className="ph-num">{formatInrMinor(child.amountDue.totalMinor)}</span>
            </div>
          </div>
          <p className="ph-caveat">
            Cambridge examination fees are charged separately at actual cost when
            {' '}{child.displayName.split(' ')[0]} registers for exams. They are not part of
            this amount.
          </p>
        </section>
      ) : null}

      {child.subjects.length > 0 ? (
        <section className="ph-section">
          <h3>
            {child.subjects.length === 1 ? 'Her subject' : `The ${child.subjects.length} subjects`}
          </h3>
          <p className="ph-hint">
            {admitted ? 'For the year ahead.' : 'These are what we are reviewing.'}
          </p>
          <div className="ph-subs">
            {child.subjects.map((subject) => (
              <span className="ph-sub" key={`${subject.subjectName}-${subject.syllabusCode}`}>
                <b>{subject.subjectName}</b>
                <span className="ph-sub-code">{subject.syllabusCode}</span>
              </span>
            ))}
          </div>
          {child.subjectGroups.length > 0 ? (
            <>
              <p className="ph-hint ph-groups-label">
                Cambridge groups covered — {coveredGroups} of {child.subjectGroups.length}
              </p>
              <div className="ph-groups">
                {child.subjectGroups.map((group) => (
                  <span className={`ph-gchip${group.covered ? ' is-on' : ''}`} key={group.name}>
                    {group.name}
                  </span>
                ))}
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      {child.payments.length > 0 ? (
        <section className="ph-section">
          <h3>What you've paid</h3>
          <p className="ph-hint">Each charge is paid in full.</p>
          <div className="ph-money">
            {child.payments.map((payment) => (
              <div className="ph-mrow" key={payment.transactionId}>
                <span>
                  {payment.purpose === 'REGISTRATION' ? 'Registration fee' : 'Subject fees for the year'}
                  <span className="ph-paidon">Paid {formatDate(payment.paidAt)}</span>
                </span>
                <span className="ph-num">
                  {formatInrMinor(payment.amountMinor)}
                  {payment.receiptId ? (
                    <Link className="ph-rcpt" to={`/receipts/${payment.transactionId}`}>Receipt</Link>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {child.counsellor ? (
        <section className="ph-section">
          <h3>Your point of contact</h3>
          <p className="ph-hint">One person, not a helpdesk queue.</p>
          <div className="ph-person">
            <span className="ph-person-av" aria-hidden="true">
              {child.counsellor.name.split(' ').map((p) => p.charAt(0)).slice(0, 2).join('')}
            </span>
            <span className="ph-person-who">
              <b>{child.counsellor.name}</b>
              <span>{child.counsellor.role}</span>
            </span>
            <Link className="btn btn-secondary btn-sm" to="/help">Get in touch</Link>
          </div>
        </section>
      ) : null}
    </ParentShell>
  );
}
